import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import SummaryApi from "../common";
import { decodeCourseId } from "../utils/encoder";

const CourseCurriculum = () => {
  const { id } = useParams();
  const courseId = decodeCourseId(id);
  const [course, setCourse] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [lessonsByCurriculum, setLessonsByCurriculum] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState(true); // NEW: Track lesson loading
  const [openCurriculumId, setOpenCurriculumId] = useState(null);

  useEffect(() => {
    if (!courseId) {
      console.error("Invalid course ID");
      setLoading(false);
      return;
    }

    fetch(SummaryApi.coursedetailsById.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ course_id: Number(courseId) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setCourse(data.data);
        }
      })
      .catch((err) => console.error("Failed to fetch course:", err));
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;

    setLoading(true);
    fetch(SummaryApi.getCurriculumByCourseId.url, {
      method: SummaryApi.getCurriculumByCourseId.method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ course_id: Number(courseId) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setCurriculum(data.data);
          if (data.data.length > 0) {
            setOpenCurriculumId(data.data[0].curriculum_id); // Open first topic by default
          }
        }
      })
      .catch((err) => console.error("Failed to fetch curriculum:", err))
      .finally(() => setLoading(false)); // Curriculum done
  }, [courseId]);

  useEffect(() => {
    if (curriculum.length === 0) {
      setLessonsByCurriculum({});
      setLoadingLessons(false);
      return;
    }

    const fetchLessonsForCurriculum = async () => {
      setLoadingLessons(true); // Start lesson loader
      const lessonsMap = {};
      for (const topic of curriculum) {
        try {
          const response = await fetch(
            SummaryApi.getCourseLessonByCurriculumId.url,
            {
              method: SummaryApi.getCourseLessonByCurriculumId.method,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ curriculum_id: topic.curriculum_id }),
            }
          );
          const data = await response.json();
          if (data.status && Array.isArray(data.data)) {
            lessonsMap[topic.curriculum_id] = data.data;
          } else {
            lessonsMap[topic.curriculum_id] = [];
          }
        } catch (error) {
          console.error(
            `Failed to fetch lessons for curriculum ${topic.curriculum_id}`,
            error
          );
          lessonsMap[topic.curriculum_id] = [];
        }
      }
      setLessonsByCurriculum(lessonsMap);
      setLoadingLessons(false); // Lessons fully loaded
    };

    fetchLessonsForCurriculum();
  }, [curriculum]);

  const toggleCurriculum = (id) => {
    setOpenCurriculumId((prevId) => (prevId === id ? null : id));
  };

  // Calculate total duration for a topic
  const getTopicDuration = (curriculumId) => {
    const lessons = lessonsByCurriculum[curriculumId] || [];
    return lessons.reduce((sum, lesson) => sum + (parseInt(lesson.duration) || 0), 0);
  };

  // Show loader until BOTH curriculum AND lessons are loaded
  if (loading || loadingLessons)
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <p className="mt-2 text-gray-600">Loading curriculum and lessons...</p>
      </div>
    );



  return (
    <div>
      <div className="p-6 max-w-8xl mx-auto bg-white shadow rounded-[5px]">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">
            {course.course_title} Curriculum
          </h1>
          <div className="text-right">
            <div className="text-sm text-gray-600">
              {curriculum.length} {curriculum.length === 1 ? "Topic" : "Topics"} |{" "}
              {Object.values(lessonsByCurriculum).reduce(
                (total, lessons) => total + lessons.length,
                0
              )}{" "}
              {Object.values(lessonsByCurriculum).reduce(
                (total, lessons) => total + lessons.length,
                0
              ) === 1
                ? "Lesson"
                : "Lessons"}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          {curriculum.map(({ curriculum_id, topic_name }) => {
            const isOpen = openCurriculumId === curriculum_id;
            const topicDuration = getTopicDuration(curriculum_id); // Auto-calculated
            return (
              <div key={curriculum_id} className="border rounded shadow-sm">
                <button
                  onClick={() => toggleCurriculum(curriculum_id)}
                  className={`w-full flex items-center justify-between px-5 py-3 font-semibold text-gray-800
                    focus:outline-none ${
                      isOpen ? "bg-gray-100" : "bg-gray-50 hover:bg-gray-100"
                    }`}
                >
                  <span>
                    {topic_name.trim()}
                    {/* Show duration even when closed */}
                    <span className="ml-2 text-sm text-gray-500">
                      ({topicDuration} mins)
                    </span>
                  </span>
                  {isOpen ? (
                    <FiChevronDown className="text-xl" />
                  ) : (
                    <FiChevronRight className="text-xl" />
                  )}
                </button>
                {isOpen && (
                  <div className="bg-white-50 border-t border-white-200 p-4">
                    {lessonsByCurriculum[curriculum_id]?.length > 0 ? (
                      <ul className="space-y-2 list-none">
                        {lessonsByCurriculum[curriculum_id].map((lesson) => (
                          <li
                            key={lesson.lesson_id}
                            className="bg-gray-50 rounded p-2 text-black flex justify-between"
                          >
                            <span>{lesson.lesson_name}</span>
                            <span className="text-sm text-gray-500">
                              {lesson.duration} mins
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="italic text-green-700">
                        No lessons found for this topic.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CourseCurriculum;